import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdRefresh, MdEdit, MdShare, MdDownload, MdClose, MdTextFields, MdArrowBack, MdImage, MdAutoFixHigh, MdVideocam } from 'react-icons/md';
import { IoSparkles } from 'react-icons/io5';
import ActionBar from './ActionBar';
import type { Action } from './ActionBar';
import FlyerPreview from './FlyerPreview';
import FlyerDetails from './FlyerDetails';
import EditCaptionsPanel from './EditCaptionsPanel';
import ImageProperties from './ImageProperties';
import { ShareModal } from '../scheduleCampaign';
import { fetchIntegrationsRequest } from '../../redux/slices/integrationsSlice';

import LoadingState from '../common/LoadingState';
import type { RootState } from '../../redux/store';
import type { ScheduleData } from '../scheduleCampaign';
import type { FlyerData } from './types';
import { regenerateFlyer, getFlyer, getCampaign } from '../../api/contentApi';
import { fetchMarketingCampaignsRequest } from '../../redux/slices/marketingCampaignSlice';
import { useSSETaskMonitor } from '../../hooks/useSSETaskMonitor';
import { PROMOFLOW_BASE_API_URL } from '../../api/endPoints';
import { downloadFileAsBlob, generateSafeFilename } from '../../utils/fileDownload';

const Playground: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const campaigns = useSelector((state: RootState) => state.marketingCampaign.campaigns);
  const [searchParams] = useSearchParams();

  // Get campaign data from location state (passed from CampaignGenerator)
  const { campaignData: initialCampaignData } = location.state || {};

  const [campaignData, setCampaignData] = useState<any>(initialCampaignData || null);
  const [fetchingCampaign, setFetchingCampaign] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCaptionsPanel, setShowCaptionsPanel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showEditDropdown, setShowEditDropdown] = useState(false);
  const [taskMonitorUrl, setTaskMonitorUrl] = useState<string | null>(null);
  const [regenerateProgress, setRegenerateProgress] = useState<number>(0);
  const [activeMediaType, setActiveMediaType] = useState<'image' | 'video'>('image');

  // Track if we've already fetched to prevent duplicate calls (even in StrictMode)
  const hasFetched = useRef(false);

  // SSE Task Monitor for regeneration
  const { status: taskStatus, message: taskMessage, isMonitoring } = useSSETaskMonitor({
    monitorUrl: taskMonitorUrl,
    enabled: !!taskMonitorUrl,
    onCompleted: async () => {
      // When regeneration completes, fetch updated flyer details
      try {
        if (flyerData && restaurantId) {
          const flyerDetails = await getFlyer(restaurantId, flyerData.id);

          if (flyerDetails && flyerDetails.length > 0) {
            const updatedFlyer = flyerDetails[0];
            setFlyerData({
              ...flyerData,
              imageUrl: updatedFlyer.flyer_url || flyerData.imageUrl
            });
          }
        }
      } catch (error) {
        console.error('Error fetching updated flyer:', error);
      } finally {
        setTaskMonitorUrl(null);
        setRegenerateProgress(0);
        setIsRegenerating(false); // Reset regenerating state
      }
    },
    onFailed: () => {
      setTaskMonitorUrl(null);
      setRegenerateProgress(0);
      setIsRegenerating(false); // Reset regenerating state
    }
  });

  // Update progress based on task status
  useEffect(() => {
    if (isMonitoring) {
      switch (taskStatus) {
        case 'processing':
          setRegenerateProgress(50);
          break;
        case 'extracting':
          setRegenerateProgress(75);
          break;
        case 'completed':
          setRegenerateProgress(100);
          break;
        default:
          setRegenerateProgress(25);
      }
    }
  }, [taskStatus, isMonitoring]);

  // Get data from Redux store
  const { generatedContent, flyerDetails, loading, ideaId: reduxIdeaId, ideaType: reduxIdeaType } = useSelector(
    (state: RootState) => state.contentGeneration
  );

  const campaignId = searchParams.get('campaignId');
  const campaignType = searchParams.get('campaignType');
  const ideaId = searchParams.get('ideaId') ? Number(searchParams.get('ideaId')) : reduxIdeaId;
  const ideaType = searchParams.get('ideaType') || reduxIdeaType;

  // Parse campaign data from GET API (NEW FLOW)
  const flyerDataFromCampaign = useMemo(() => {
    if (!campaignData) return null;

    const flyerImageUrl = campaignData.flyer?.image?.image?.storage_url || campaignData.flyer?.image?.storage_url || '';

    const socialCaptions: Record<string, import('./types').SocialMediaContent> = {};

    if (campaignData.channels && Array.isArray(campaignData.channels)) {
      campaignData.channels.forEach((channel: any) => {
        const platformName = channel.name;
        socialCaptions[platformName] = {
          post_copy: channel.caption || '',
          subject_line: channel.subject, // For Email
          visual_brief: undefined
        };
      });
    }

    return {
      id: campaignData.id || '',
      imageUrl: flyerImageUrl,
      headline: campaignData.title || '',
      description: campaignData.campaignData?.description || campaignData.title || '',
      offerDetails: campaignData.campaignData?.offerDetails || '',
      footer: campaignData.campaignData?.tagLine || '',
      prompt: '',
      socialCaptions,
      contentIds: {}
    };
  }, [campaignData]);

  // Extract image properties from campaign data
  const imageProperties = useMemo(() => {
    if (!campaignData) return null;

    const props = campaignData.flyer?.image?.image?.properties || campaignData.flyer?.image?.properties;

    if (!props) return null;

    return {
      width: props.width,
      height: props.height,
      aspect_ratio: props.aspect_ratio,
      resolution: props.resolution,
      orientation: props.orientation,
      format: props.format,
      size_bytes: props.kb ? parseFloat(props.kb) * 1024 : 0 // Convert KB to bytes
    };
  }, [campaignData]);

  // Extract video URL and properties from campaign data
  const videoData = useMemo(() => {
    if (!campaignData) return null;

    const videoUrl = campaignData.flyer?.video?.video?.storage_url || campaignData.flyer?.video?.storage_url;
    const props = campaignData.flyer?.video?.video?.properties || campaignData.flyer?.video?.properties;

    if (!videoUrl) return null;

    return {
      url: videoUrl,
      properties: props ? {
        duration_seconds: props.duration,
        width: props.width,
        height: props.height,
        aspect_ratio: props.aspect_ratio,
        frame_rate: props.frame_rate,
        bitrate_kbps: props.bitrate_kbps,
        codec: props.codec,
        resolution: props.resolution,
        orientation: props.orientation,
        format: props.format,
        size_bytes: props.size ? parseFloat(props.size) * 1024 : 0 // Convert KB to bytes
      } : null
    };
  }, [campaignData]);

  // Parse and build flyer data from API response (OLD FLOW)
  const flyerDataFromAPI = useMemo(() => {
    if (!generatedContent || !flyerDetails) return null;

    // Parse seasonal flyer content payload
    let flyerContent: any = {};
    if (generatedContent.seasonal_flyer && generatedContent.seasonal_flyer.length > 0) {
      try {
        flyerContent = JSON.parse(generatedContent.seasonal_flyer[0].content_payload);
      } catch (error) {
        console.error('Error parsing flyer content:', error);
      }
    }

    // Parse social media captions with full details - dynamically capture all platforms
    const socialCaptions: Record<string, import('./types').SocialMediaContent> = {};
    const contentIds: Record<string, string> = {}; // Map platform to content_id

    if (generatedContent.social_media) {
      generatedContent.social_media.forEach((item) => {
        try {
          const payload = JSON.parse(item.content_payload);
          const channel = item.primary_channel;

          const itemAny = item as any;
          contentIds[channel] = itemAny.uuid || itemAny.content_uuid || item.content_id?.toString() || '';
          socialCaptions[channel] = {
            post_copy: payload.post_copy || '',
            subject_line: payload.subject_line,
            visual_brief: payload.visual_brief ? {
              image_prompt: payload.visual_brief.image_prompt || '',
              image_style: payload.visual_brief.image_style || '',
              aspect_ratio: payload.visual_brief.aspect_ratio || '',
              video_idea: payload.visual_brief.video_idea || ''
            } : undefined
          };
        } catch (error) {
          console.error('Error parsing social media content:', error);
        }
      });
    }

    return {
      id: flyerDetails.content_id?.toString() || '1',
      imageUrl: flyerDetails.flyer_url || '',
      headline: flyerContent.Header || '',
      description: flyerContent.Description || '',
      offerDetails: flyerContent['Offer Details'] || '',
      footer: flyerContent.Footer || '',
      prompt: flyerContent.hero_image_prompt || '',
      socialCaptions,
      contentIds
    };
  }, [generatedContent, flyerDetails]);

  const [flyerData, setFlyerData] = useState<FlyerData | null>(null);

  // Fetch campaign data if campaignId is in URL params (new flow)
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!campaignId || !restaurantId || initialCampaignData) return; // Skip if already have data

      setFetchingCampaign(true);
      try {
        const data = await getCampaign(campaignId, restaurantId);

        if (data.success && data.data) {
          setCampaignData(data.data);
        }
      } catch (error) {
        console.error('[Playground] Error fetching campaign:', error);
      } finally {
        setFetchingCampaign(false);
      }
    };

    fetchCampaignData();
  }, [campaignId, restaurantId, initialCampaignData]);

  // Update flyerData - prioritize campaign data over legacy API data
  useEffect(() => {
    if (flyerDataFromCampaign) {
      setFlyerData(flyerDataFromCampaign);
    } else if (flyerDataFromAPI) {
      setFlyerData(flyerDataFromAPI);
    }
  }, [flyerDataFromCampaign, flyerDataFromAPI]);

  // Fetch integrations on mount only once
  useEffect(() => {
    if (!hasFetched.current && restaurantId) {
      hasFetched.current = true;
      dispatch(fetchIntegrationsRequest({ restaurantId }));
    }
  }, [restaurantId]);

  const handleRegenerateFlyer = async () => {
    if (!flyerData || !restaurantId) return;

    try {
      setIsRegenerating(true);

      const flyerContent = {
        Header: flyerData.headline,
        Description: flyerData.description,
        'Offer Details': flyerData.offerDetails,
        Footer: flyerData.footer,
        hero_image_prompt: flyerData.prompt
      };

      // Step 3: Start task monitor only after flyers API succeeds
      if (ideaId && ideaType) {
        // Build task monitor URL with correct format
        const monitorUrl = `${PROMOFLOW_BASE_API_URL.triggerUrl}/task-monitor?restaurantId=${restaurantId}&ideaId=${ideaId}&ideaType=${ideaType}`;
        setTaskMonitorUrl(monitorUrl);
        setRegenerateProgress(10);
      } else {
        console.warn('No idea_id or idea_type available in Redux state');
      }

    } catch (error) {
      console.error('Error regenerating flyer:', error);
      setIsRegenerating(false);
    } finally {

    }
  };

  // Navigate to the Campaign Creation UI in "update" mode, passing the current
  // campaign data along so the form can be prefilled and the flyer preview shown.
  const goToUpdateCampaign = (mode: 'text_only' | 'full_update' | 'image_only') => {
    if (!campaignId || !campaignData) return;
    navigate(`/${restaurantId}/campaign-creation`, {
      state: {
        editMode: true,
        updateMode: mode,
        campaignId,
        campaignData
      }
    });
  };

  const handleEditContent = () => {
    if (campaignId) {
      goToUpdateCampaign('text_only');
    } else {
      setShowDetails(!showDetails);
    }
  };

  const handleRegenerateWithModal = () => {
    if (campaignId) {
      goToUpdateCampaign('full_update');
    } else {
      setShowDetails(!showDetails);
    }
  };

  const handleChangeImage = () => {
    if (campaignId) {
      goToUpdateCampaign('image_only');
    }
  };

  const handleEditDetails = () => {
    handleEditContent();
  };

  const handleEditCaptions = () => {
    setShowCaptionsPanel(true);
  };

  const handleSaveCaptions = (captions: any) => {
    if (flyerData) {
      setFlyerData({
        ...flyerData,
        socialCaptions: captions
      });
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handlePostNow = () => {
    console.log('Posting campaign now...');
  };

  const handleSchedule = (data: ScheduleData) => {
    console.log('Scheduling campaign:', data);
    // Add schedule logic here
  };

  const handleDownload = async () => {
    if (!flyerData || !flyerData.imageUrl) {
      console.error('No flyer image available to download');
      return;
    }

    // Generate filename from headline or use default
    const filename = flyerData.headline
      ? generateSafeFilename(flyerData.headline, 'jpeg')
      : 'flyer.jpeg';

    // Use utility function to download file
    await downloadFileAsBlob(flyerData.imageUrl, filename);
  };

  // Check if campaign has been published (has publishData)
  const hasPublishData = React.useMemo(() => {
    const campaignDataAny = campaignData as any;
    return campaignDataAny?.publishData && 
           Array.isArray(campaignDataAny.publishData) && 
           campaignDataAny.publishData.length > 0;
  }, [campaignData]);

  // Custom actions for the action bar
  const actions: Action[] = [
    {
      id: 'edit',
      icon: <MdEdit size={20} />,
      label: 'Edit Flyer',
      dropdownActions: [
        {
          id: 'edit-image',
          icon: <MdImage size={20} />,
          label: 'Change Image',
          onClick: handleChangeImage
        },
        {
          id: 'edit-content',
          icon: <MdAutoFixHigh size={20} />,
          label: 'Edit Content',
          onClick: handleEditContent
        },
        {
          id: 'regenerate',
          icon: <IoSparkles size={20} />,
          label: 'Regenerate',
          onClick: handleRegenerateWithModal
        }
      ]
    },
    {
      id: 'edit-captions',
      icon: <MdTextFields size={20} />,
      label: 'Edit Captions',
      onClick: handleEditCaptions
    },
    // Only show Share button if campaign hasn't been published
    ...(!hasPublishData ? [{
      id: 'share',
      icon: <MdShare size={20} />,
      label: 'Share',
      onClick: handleShare
    }] : []),
    {
      id: 'download',
      icon: <MdDownload size={20} />,
      label: 'Download',
      onClick: handleDownload
    }
  ];

  // Show loading state until data is ready
  if (loading || fetchingCampaign || !flyerData) {
    return <LoadingState message={fetchingCampaign ? "Fetching campaign details..." : "Loading flyer content..."} />;
  }

  return (
    <div className="min-h-screen relative">

      <div className="max-w-7xl mx-auto">
        {/* Header Row: Back Button (Left) */}
        <div className="mb-8 flex items-center justify-between gap-4">
          {/* Back Button - Left Side */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              // Refresh campaigns list before navigating back
              if (restaurantId) {
                dispatch(fetchMarketingCampaignsRequest(restaurantId));
              }
              
              navigate(`/${restaurantId}/dashboard`);
            }}
            className="px-3 py-2 cursor-pointer text-white font-medium rounded-lg flex items-center gap-2 transition-colors backdrop-blur-sm border border-gray-600/30"
            style={{
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 14px 0 #0000001a',
              background: '#212121',
              border: '1px solid #e0e0e021'
            }}
          >
            <MdArrowBack size={20} />
          </motion.button>
        </div>

        {/* Main Content */}
        <div className={`relative grid gap-8 items-start transition-all duration-500 ${showDetails ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
          }`}>
          {/* Flyer Preview + Properties - Centered when details hidden, left side when details shown */}
          <motion.div
            className={`flex gap-6 items-start ${!showDetails ? 'mx-auto justify-center' : ''
              }`}
            layout
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            {/* Media Tabs - Show only if both image and video are available */}
            {videoData && (
              <div className="flex flex-col gap-2 pt-4">
                {/* Image Tab */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveMediaType('image')}
                  className={`p-3 rounded-lg transition-all cursor-pointer ${activeMediaType === 'image'
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-800/50 text-white hover:bg-gray-700/50'
                    }`}
                  title="Image"
                >
                  <MdImage size={24} />
                </motion.button>

                {/* Video Tab */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveMediaType('video')}
                  className={`p-3 rounded-lg transition-all cursor-pointer ${activeMediaType === 'video'
                      ? 'bg-brand-primary text-white'
                      : 'bg-gray-800/50 text-white hover:bg-gray-700/50'
                    }`}
                  title="Video"
                >
                  <MdVideocam size={24} />
                </motion.button>
              </div>
            )}

            {/* Flyer Image or Video */}
            <div className="relative flex-shrink-0">
              <AnimatePresence mode="wait">
                {activeMediaType === 'image' ? (
                  <motion.div
                    key="image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FlyerPreview imageUrl={flyerData.imageUrl} />
                  </motion.div>
                ) : videoData ? (
                  <motion.div
                    key="video"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-center"
                  >
                    <div
                      className="relative max-w-md w-full overflow-hidden shadow-2xl bg-gray-900"
                      style={{ width: '448px', height: '597px' }}
                    >
                      <video
                        src={videoData.url}
                        autoPlay
                        loop
                        playsInline
                        className="absolute top-0 left-0 w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              {/* Regeneration Progress Overlay */}
              <AnimatePresence>
                {isMonitoring && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center"
                  >
                    <div className="text-center space-y-4">
                      <div className="relative w-24 h-24">
                        <svg className="w-24 h-24 transform -rotate-90">
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="6"
                            fill="none"
                          />
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="#EE4423"
                            strokeWidth="6"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 40}`}
                            strokeDashoffset={`${2 * Math.PI * 40 * (1 - regenerateProgress / 100)}`}
                            strokeLinecap="round"
                            className="transition-all duration-500"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-white text-lg font-semibold">{regenerateProgress}%</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-white font-medium text-lg">
                          {regenerateProgress < 30 ? 'Starting generation...' :
                            regenerateProgress < 60 ? 'Processing flyer...' :
                              regenerateProgress < 90 ? 'Finalizing...' :
                                'Almost done!'}
                        </p>
                        {taskMessage && (
                          <p className="text-gray-400 text-sm mt-1 capitalize">{taskMessage}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-2">This may take a few moments</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Properties & ActionBar - Right side of flyer, hide when edit mode is active */}
            {!showDetails && (
              <div className="flex gap-4 items-start">
                {/* Properties */}
                <div className="w-80 flex-shrink-0">
                  {activeMediaType === 'image' && imageProperties ? (
                    <ImageProperties properties={imageProperties} />
                  ) : activeMediaType === 'video' && videoData?.properties ? (
                    <ImageProperties properties={videoData.properties} />
                  ) : null}
                </div>
                
                {/* Action Bar - Vertical */}
                <div className="flex flex-col gap-4">
                  {/* Close Edit Button - Shows above ActionBar when edit panel would be open */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -10 }}
                        transition={{ duration: 0.2 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleEditDetails}
                        className="p-4 bg-surface-dark cursor-pointer hover:bg-gray-700/80 text-white font-medium rounded-full flex items-center justify-center gap-2 transition-colors backdrop-blur-sm shadow-lg border border-gray-600/30 mx-auto"
                      >
                        <MdClose size={20} />
                      </motion.button>
                    )}
                  </AnimatePresence>

                  {/* Action Bar */}
                  <ActionBar actions={actions} className="" />
                </div>
              </div>
            )}
          </motion.div>

          {/* Right: Flyer Details - Animated entry */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="bg-gray-900/50 rounded-md p-4 flex flex-col max-h-[calc(90vh-180px)] overflow-hidden"
                style={{
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 0 14px 0 #0000001a',
                  background: 'linear-gradient(90deg, #212121eb, #373737eb)',
                  border: '1px solid #e0e0e021'
                }}
              >
                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                  <FlyerDetails
                    data={flyerData!}
                    onEdit={(field, value) => {
                      if (flyerData) {
                        setFlyerData({
                          ...flyerData,
                          [field]: value
                        });
                      }
                    }}
                  />
                </div>

                {/* Regenerate Button - Only shown when details are visible */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ scale: isRegenerating ? 1 : 1.05 }}
                  whileTap={{ scale: isRegenerating ? 1 : 0.95 }}
                  onClick={handleRegenerateFlyer}
                  disabled={isRegenerating}
                  className={`mt-4 px-8 py-3 text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-colors flex-shrink-0 ${isRegenerating
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-brand-primary hover:bg-[#d63d20]'
                    }`}
                >
                  <MdRefresh size={24} className={isRegenerating ? 'animate-spin' : ''} />
                  {isRegenerating ? 'Regenerating...' : 'Regenerate Flyer'}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Edit Captions Panel */}
      {restaurantId && (
        <EditCaptionsPanel
          isOpen={showCaptionsPanel}
          onClose={() => setShowCaptionsPanel(false)}
          captions={flyerData.socialCaptions || {}}
          contentIds={flyerData.contentIds || {}}
          restaurantId={restaurantId}
          campaignId={campaignId || undefined}
          onSave={handleSaveCaptions}
        />
      )}

      {/* Share Modal */}
      {restaurantId && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          restaurantId={restaurantId}
          flyerImageUrl={flyerData.imageUrl}
          generatedContentId={flyerDetails?.content_id ? Number(flyerDetails.content_id) : undefined}
          flyerId={flyerData.id ? Number(flyerData.id) : undefined}
          campaignId={campaignId || undefined}
          onPostNow={handlePostNow}
          onSchedule={handleSchedule}
          mediaProperties={activeMediaType === 'image' ? imageProperties : videoData?.properties}
          mediaType={activeMediaType}
        />
      )}

    </div>
  );
};

export default Playground;
